import React, {
	useEffect,
	useState,
	useCallback,
	useMemo,
	useRef,
	type KeyboardEvent,
	type MouseEvent,
	useImperativeHandle,
	type Ref,
} from 'react';
import debounce from 'lodash/debounce';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Pressable, xcss } from '@atlaskit/primitives';
import { components, type OptionProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import type { JwmCategoryOptionsFragmentColor } from '@atlassian/jira-business-categories/src/common/graphql/types.tsx';
import type { CategorySelectOption } from '@atlassian/jira-business-categories/src/common/types.tsx';
import { toCategorySelectOption } from '@atlassian/jira-business-categories/src/common/utils/category/index.tsx';
import { CATEGORY_FORM_WIDTH } from '@atlassian/jira-business-categories/src/ui/category-form/constants.tsx';
import CategoryForm from '@atlassian/jira-business-categories/src/ui/category-form/index.tsx';
import type { CategoryData } from '@atlassian/jira-business-categories/src/ui/category-form/types.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import {
	getPopupComponentWithHeight,
	PopupComponent,
} from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { CATEGORY_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { DEFAULT_ROW_HEIGHT, LIST_FIELD_POPUP_OFFSET } from '../../../../common/constants.tsx';
import LazyCreatableSelect from '../../../../common/ui/lazy-creatable-select/index.tsx';
import { CellContentWrapper } from '../../../../common/ui/list-cell/styled.tsx';
import CreateErrorFlag, { CREATE_CATEGORY_ERROR_FLAG_ID } from './create-error-flag/index.tsx';
import CustomSelectOption from './custom-select-option/main.tsx';
import messages from './messages.tsx';
import type { EditCategoryCellProps } from './types.tsx';

export const isCategoryField = (
	field?: {
		type: string;
	} | null,
) => field != null && field.type === CATEGORY_TYPE;

// Creating type guard because Typescript bug with filtering readonly array
// https://github.com/microsoft/TypeScript/issues/17002#issuecomment-494937708
const isReadOnlyArray = (param: unknown | readonly unknown[] | null): param is readonly unknown[] =>
	Array.isArray(param);

const LOAD_OPTIONS_DEBOUNCE_TIMER = 300;
const POPUP_OFFSET: [number, number] = LIST_FIELD_POPUP_OFFSET;
const POPUP_OFFSET_NEW: [number, number] = [0, -DEFAULT_ROW_HEIGHT];

type EditCategoryCellState =
	| { status: 'idle' }
	| {
			status: 'updating-category';
			categoryName: string;
			categoryOptionId: string;
			categoryColor: JwmCategoryOptionsFragmentColor;
	  }
	| { status: 'creating-category'; categoryToBeCreated: string };

export const EditCategoryCell = ({
	width,
	issueTypeId,
	categoryState,
	categoryOptions,
	triggerContent,
	setCategoryToDeleteInModal,
	onCategoryCellValueChange,
	onCategoryUpdate,
	onCategoryCreate,
	onClose,
	wrapperRef,
	cellRef,
	inputValue,
	onInputChange,
}: EditCategoryCellProps) => {
	const [defaultCategoryOptions, setDefaultCategoryOptions] =
		useState<CategorySelectOption[]>(categoryOptions);

	const [editCategoryCellState, setEditCategoryCellState] = useState<EditCategoryCellState>({
		status: 'idle',
	});
	const [triggerRef, setTriggerRef] = useState<HTMLElement | null>();

	const { showFlag } = useFlagService();

	const project = useProject();
	const hasAdminPermission = project.permissions.administerProject;

	const { formatMessage } = useIntl();
	const isCreateOptionFocused = useRef(false);

	const onPopupClose = useCallback(
		(event?: Event | MouseEvent | KeyboardEvent) => {
			if (
				!fg('jira_list_keyboard_nav') ||
				!event ||
				!(event.target instanceof HTMLElement && triggerRef && event.target.contains(triggerRef))
			) {
				onClose();
				setEditCategoryCellState({
					status: 'idle',
				});
				wrapperRef &&
					setTimeout(() => {
						wrapperRef?.current?.focus();
					}, 0);
			}
		},
		[onClose, wrapperRef, triggerRef],
	);

	const searchCategories = useCallback(
		(query = '') => {
			// TODO: JET-3807 Replace with back end fetch API
			const filteredCategories = categoryOptions.filter((categoryOption) =>
				categoryOption?.label.toLowerCase().startsWith(query.toLowerCase()),
			);
			return Promise.resolve(filteredCategories);
		},
		[categoryOptions],
	);

	useEffect(() => {
		setDefaultCategoryOptions(categoryOptions);
	}, [categoryOptions]);

	const loadOptionsDebounced = useCallback<typeof searchCategories>(
		(...args) => {
			const debounced = debounce<typeof searchCategories>(
				searchCategories,
				LOAD_OPTIONS_DEBOUNCE_TIMER,
				{
					leading: true,
				},
			);
			return debounced(...args);
		},
		[searchCategories],
	);

	const showErrorFlag = useCallback(() => {
		showFlag({
			id: CREATE_CATEGORY_ERROR_FLAG_ID,
			render: () => <CreateErrorFlag />,
		});
	}, [showFlag]);

	const handleChange = useCallback(
		(newOption: CategorySelectOption | readonly CategorySelectOption[] | null) => {
			if (isReadOnlyArray(newOption)) {
				// Select component supports both single and multiple select
				// for categories we don't allow multiple select
				return;
			}

			const isValueEqual = newOption?.value === categoryState?.optionId;
			// handleChange is triggered when we click edit a category
			// in which cases we don't want to saveAndClose
			if (isValueEqual || editCategoryCellState.status !== 'idle') {
				return;
			}

			onCategoryCellValueChange(
				newOption
					? {
							optionId: newOption.value,
							value: newOption.label,
							color: newOption.color,
						}
					: null,
			);
			onPopupClose();
		},
		[
			categoryState?.optionId,
			editCategoryCellState.status,
			onCategoryCellValueChange,
			onPopupClose,
		],
	);

	const updateEditingCategory = useCallback(
		(
			categoryLabel: string | null,
			categoryValue: string | null,
			categoryColor: JwmCategoryOptionsFragmentColor,
		) => {
			setEditCategoryCellState({
				status: 'updating-category',
				categoryName: categoryLabel ?? '',
				categoryOptionId: categoryValue ?? '',
				categoryColor: categoryColor ?? null,
			});
		},
		[],
	);

	const handleCreate = useCallback(
		(categoryName: string) => {
			if (!hasAdminPermission) {
				showErrorFlag();
				return;
			}
			setEditCategoryCellState({
				status: 'creating-category',
				categoryToBeCreated: categoryName,
			});
		},
		[hasAdminPermission, showErrorFlag],
	);

	const onNewCategoryFormSubmit = useCallback(
		(newCategory: CategoryData) => {
			onCategoryCreate(newCategory.categoryName, newCategory.categoryColor);
			onPopupClose();
			setEditCategoryCellState({ status: 'idle' });
		},
		[onCategoryCreate, onPopupClose],
	);

	const onEditCategoryFormSubmit = useCallback(
		(updatedCategory: CategoryData) => {
			if (editCategoryCellState.status === 'updating-category') {
				onCategoryUpdate({
					optionId: editCategoryCellState.categoryOptionId,
					newCategoryName: updatedCategory.categoryName,
					newCategoryColor: updatedCategory.categoryColor,
				});
				onPopupClose();
				setEditCategoryCellState({ status: 'idle' });
			}
		},
		[onCategoryUpdate, onPopupClose, editCategoryCellState],
	);

	const getCreateCategoryLabel = useCallback(
		(categoryInputValue: string): string =>
			`${categoryInputValue} (${formatMessage(messages.createNewCategory)})`,
		[formatMessage],
	);

	const trigger = useCallback(
		(triggerProps: TriggerProps) => {
			const ref: Ref<HTMLElement> = (node) => {
				setTriggerRef(node);
				if (typeof triggerProps.ref === 'function') {
					triggerProps.ref(node);
				}
			};
			return fg('jira_list_checkbox_convert_to_pressable') ? (
				<Pressable {...triggerProps} xcss={popupTriggerStyles} ref={ref}>
					{triggerContent}
				</Pressable>
			) : (
				<div {...triggerProps} ref={ref} role="button">
					{triggerContent}
				</div>
			);
		},
		[triggerContent],
	);

	const getCustomComponents = useCallback(
		(onContentUpdate: () => void) => ({
			Option: (optionProps: OptionProps) => {
				const { label, value, data: optionPropsData, isFocused } = optionProps;
				const { __isNew__: isCreateOption } = optionPropsData;
				isCreateOptionFocused.current = isCreateOption && isFocused;

				return (
					<components.Option {...optionProps}>
						<CustomSelectOption
							label={label}
							value={value}
							color={optionPropsData?.color}
							isFocused={isFocused}
							isCreateOption={isCreateOption}
							issueTypeId={issueTypeId}
							onCategoryIsEditing={(categoryLabel, categoryValue, categoryColor) => {
								updateEditingCategory(categoryLabel, categoryValue, categoryColor);
								onContentUpdate();
							}}
							onCategoryUpdate={onCategoryUpdate}
							categoryBeenEdited={
								editCategoryCellState.status === 'updating-category'
									? editCategoryCellState.categoryName
									: null
							}
							wrapperRef={wrapperRef}
							selectedOption={categoryState?.value ?? ''}
							setCategoryToDeleteInModal={setCategoryToDeleteInModal}
						/>
					</components.Option>
				);
			},
		}),
		[
			issueTypeId,
			updateEditingCategory,
			onCategoryUpdate,
			editCategoryCellState,
			wrapperRef,
			categoryState?.value,
			setCategoryToDeleteInModal,
		],
	);

	const styles = useMemo(
		() => ({
			dropdownIndicator: (base: Record<string, string>) => ({
				...base,
				display: 'none',
			}),
			option: (base: Record<string, string>) => ({
				...base,
				padding: '0px',
				// A grey background is applied when hard presing on the edit/delete category control buttons
				':active': {
					backgroundColor: 'none',
				},
				...(editCategoryCellState.status === 'updating-category' && {
					boxShadow: 'none',
					background: 'none',
				}),
			}),
			container: (base: Record<string, string>) => ({
				...base,
				cursor: `${editCategoryCellState.status === 'updating-category' ? 'default' : 'pointer'}`,
				background: token('elevation.surface', 'transparent'),
			}),
		}),
		[editCategoryCellState.status],
	);

	const noOptionMessage = useCallback(() => formatMessage(messages.noMatches), [formatMessage]);

	const PopupComponentBasedOnOptionsHeight = useMemo(
		() =>
			getPopupComponentWithHeight({
				numOptions: defaultCategoryOptions.length,
				hasFooter: true,
			}),
		[defaultCategoryOptions],
	);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLDivElement>) => {
			if (editCategoryCellState.status !== 'idle') {
				return;
			}
			if (e.key === 'Escape') {
				wrapperRef?.current?.focus();
			}
		},
		[editCategoryCellState.status, wrapperRef],
	);

	const handleKeyDownNew = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (editCategoryCellState.status !== 'idle') {
				return true;
			}
			if (event.code === 'Tab') {
				return true;
			}
			if (event.code === 'Enter' && isCreateOptionFocused.current) {
				return true;
			}
			return false;
		},
		[editCategoryCellState.status],
	);

	const handleBlur = useCallback(() => true, []);

	useImperativeHandle(cellRef, () => ({ onKeyDown: handleKeyDownNew, onBlur: handleBlur }), [
		handleKeyDownNew,
		handleBlur,
	]);

	const categoryEditData: CategoryData = useMemo(
		() =>
			editCategoryCellState.status === 'updating-category'
				? {
						categoryName: editCategoryCellState.categoryName,
						categoryColor: editCategoryCellState.categoryColor,
					}
				: {
						categoryName: '',
						categoryColor: null,
					},
		[editCategoryCellState],
	);

	const popupContent = useCallback(
		({ update: onContentUpdate }: { update: () => void }) => (
			<>
				<CellContentWrapper
					// -1px border left from gridcell
					preferredWidth={
						editCategoryCellState.status === 'idle' ? width - 1 : CATEGORY_FORM_WIDTH - 1
					}
					data-testid="business-list.ui.list-view.category-cell.edit-category-cell.category-wrapper"
					onKeyDown={fg('jira_list_keyboard_nav') ? undefined : handleKeyDown}
				>
					<LazyCreatableSelect
						autoFocus
						value={toCategorySelectOption(categoryState)}
						label={
							editCategoryCellState.status === 'creating-category'
								? editCategoryCellState.categoryToBeCreated
								: formatMessage(messages.placeholder)
						}
						placeholder={
							editCategoryCellState.status === 'creating-category'
								? editCategoryCellState.categoryToBeCreated
								: formatMessage(messages.placeholder)
						}
						// @ts-expect-error - TS2322 - Property 'color' is missing in type 'SelectOption' but required in type 'CategorySelectOption'.
						onChange={handleChange}
						isClearable
						isMulti={false}
						isDisabled={editCategoryCellState.status !== 'idle'}
						noOptionsMessage={noOptionMessage}
						menuIsOpen={editCategoryCellState.status === 'idle'}
						onCreateOption={(categoryName) => {
							handleCreate(categoryName);
							onContentUpdate();
						}}
						formatCreateLabel={getCreateCategoryLabel}
						defaultOptions={defaultCategoryOptions}
						loadOptions={loadOptionsDebounced}
						components={getCustomComponents(onContentUpdate)}
						// @ts-expect-error - TS2322 - Type '{ dropdownIndicator: (base: any) => any; option: (base: any) => any; container: (base: any) => any; }' is not assignable to type '{ [styleKey: string]: () => { display?: string | undefined; color?: string | undefined; background?: string | undefined; boxShadow?: string | undefined; cursor?: string | undefined; padding?: string | undefined; ":active"?: { ...; } | undefined; }; }'.
						styles={styles}
						inputValue={inputValue}
						onInputChange={onInputChange}
					/>
				</CellContentWrapper>
				{editCategoryCellState.status !== 'idle' && (
					<CategoryForm
						initialData={
							editCategoryCellState.status === 'creating-category'
								? {
										categoryName: editCategoryCellState.categoryToBeCreated,
										categoryColor: null,
									}
								: categoryEditData
						}
						type={editCategoryCellState.status === 'creating-category' ? 'create' : 'edit'}
						onCancel={() => {
							setEditCategoryCellState({ status: 'idle' });
							onContentUpdate();
						}}
						onSubmit={
							editCategoryCellState.status === 'creating-category'
								? onNewCategoryFormSubmit
								: onEditCategoryFormSubmit
						}
					/>
				)}
			</>
		),
		[
			inputValue,
			onInputChange,
			editCategoryCellState,
			width,
			handleKeyDown,
			categoryState,
			formatMessage,
			handleChange,
			noOptionMessage,
			handleCreate,
			getCreateCategoryLabel,
			defaultCategoryOptions,
			loadOptionsDebounced,
			getCustomComponents,
			styles,
			categoryEditData,
			onNewCategoryFormSubmit,
			onEditCategoryFormSubmit,
		],
	);

	return (
		<Popup
			testId="business-list.ui.list-view.category-cell.edit-category-cell.popup"
			isOpen
			placement="bottom-start"
			autoFocus={false}
			offset={fg('jira_list_keyboard_nav') ? POPUP_OFFSET_NEW : POPUP_OFFSET}
			onClose={onPopupClose}
			popupComponent={
				// PopupComponentBasedOnOptionsHeight is used to calculate the height to accommodate total of options in the dropdown
				// when the editCategoryCellState is changed to non `idle`, to prevent cutoff of the `CategoryForm`, which can be taller than the total of options in the dropdown
				// we want to recalculate the height based on the content of the popup, which is the default behavior of the PopupComponent
				editCategoryCellState.status === 'idle'
					? PopupComponentBasedOnOptionsHeight
					: PopupComponent
			}
			trigger={trigger}
			content={popupContent}
		/>
	);
};

const popupTriggerStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
});
